import { useEffect, useRef, useState } from 'react';
import styles from './ChatPage.module.css';

/*
 * Maps backend statuses to chat flow steps.
 *
 * context_loading  → steps 0–1 (timer auto-advances at 3s)
 * llm_running      → step 2
 * tool_running     → step 3 (label updates with toolName)
 */

const BASE_STEPS = [
  { id: 'workspace',  label: 'Loading workspace',      detail: 'Fetching reports, actions and channel context' },
  { id: 'context',    label: 'Building Zeqi context',    detail: 'Structuring data for the model' },
  { id: 'llm',        label: 'Analyzing',               detail: 'Zeqi is reasoning over your data' },
  { id: 'tool',       label: 'Looking up data',         detail: 'Running a report lookup' },
];

function buildSteps(statusPayload) {
  const status = statusPayload?.status || null;

  if (!status || status === 'streaming') return null;

  if (status === 'context_loading') {
    return BASE_STEPS.map((s, i) => ({
      ...s,
      status: i === 0 ? 'active' : 'pending',
    }));
  }

  if (status === 'llm_running') {
    return BASE_STEPS.map((s, i) => {
      if (i < 2) return { ...s, status: 'done' };
      if (i === 2) return { ...s, status: 'active' };
      return { ...s, status: 'pending' };
    });
  }

  if (status === 'tool_running') {
    const toolLabel = statusPayload?.toolName
      ? `Looking up: ${String(statusPayload.toolName).replace(/_/g, ' ')}`
      : 'Looking up data';
    return BASE_STEPS.map((s, i) => {
      if (i < 3) return { ...s, status: 'done' };
      if (i === 3) return { ...s, label: toolLabel, status: 'active' };
      return { ...s, status: 'pending' };
    });
  }

  return null;
}

/* ─── Elapsed timer ─────────────────────────────────────────────────────── */

const ElapsedTimer = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt || elapsed < 1) return null;
  return <span className={styles.chatFlowTimer}>{elapsed}s</span>;
};

/* ─── Step icon ─────────────────────────────────────────────────────────── */

const StepIcon = ({ status }) => {
  if (status === 'done') {
    return (
      <span className={`${styles.chatFlowIcon} ${styles.chatFlowIconDone}`}>
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className={`${styles.chatFlowIcon} ${styles.chatFlowIconActive}`}>
        <span className={styles.chatFlowSpinner} />
      </span>
    );
  }
  return <span className={`${styles.chatFlowIcon} ${styles.chatFlowIconPending}`} />;
};

/* ─── Hook ──────────────────────────────────────────────────────────────── */

export function useChatStatusFlow(statusPayload) {
  const [steps, setSteps] = useState(null);
  const [activeStartedAt, setActiveStartedAt] = useState(null);
  const timerRef = useRef(null);
  const prevStatusRef = useRef(null);

  useEffect(() => {
    const status = statusPayload?.status || null;

    if (!status || status === 'streaming') {
      clearTimeout(timerRef.current);
      setSteps(null);
      setActiveStartedAt(null);
      prevStatusRef.current = null;
      return;
    }

    const derived = buildSteps(statusPayload);
    setSteps(derived);

    // Track when the active step began
    if (status !== prevStatusRef.current) {
      setActiveStartedAt(Date.now());
      prevStatusRef.current = status;
    }

    // During context_loading: auto-advance step 0 → step 1 after 3s
    if (status === 'context_loading') {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setSteps((prev) => {
          if (!prev) return prev;
          return prev.map((s, i) => {
            if (i === 0) return { ...s, status: 'done' };
            if (i === 1) return { ...s, status: 'active' };
            return s;
          });
        });
      }, 3000);
    } else {
      clearTimeout(timerRef.current);
    }

    return () => clearTimeout(timerRef.current);
  }, [statusPayload]);

  return { steps, activeStartedAt };
}

/* ─── Component ─────────────────────────────────────────────────────────── */

export function ChatStatusFlow({ statusPayload }) {
  const { steps, activeStartedAt } = useChatStatusFlow(statusPayload);

  if (!steps) return null;

  // Only render steps up to and including the first active one (hide pending)
  const visibleCount = steps.findIndex((s) => s.status === 'active') + 1;
  const visible = visibleCount > 0 ? steps.slice(0, visibleCount) : steps.filter((s) => s.status === 'done');

  if (!visible.length) return null;

  return (
    <div className={styles.chatFlowLog}>
      {visible.map((step) => (
        <div key={step.id} className={`${styles.chatFlowStep} ${styles[`chatFlowStep_${step.status}`]}`}>
          <StepIcon status={step.status} />
          <div className={styles.chatFlowStepBody}>
            <span className={styles.chatFlowStepLabel}>{step.label}</span>
            {step.status === 'active' && <ElapsedTimer startedAt={activeStartedAt} />}
          </div>
        </div>
      ))}
    </div>
  );
}
